import { Card, CardSection, Image, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import type { BeholdPost } from "@/lambda/social/types";

/** Square media column; card height matches media (text uses line-clamp inside). */
const MEDIA_SIZE = 180;

/** Caption lines that fit in the text column beside 180px media (incl. padding + hashtags). */
export function captionLineClamp(hashtagCount: number): number {
  return hashtagCount > 0 ? 4 : 6;
}

export default function InstagramCard(post: BeholdPost) {
  const [isHovered, setIsHovered] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { id, prunedCaption, sizes, permalink, hashtags, mediaType, mediaUrl, altText } = post;

  const isVideo = mediaType === "VIDEO";

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current && videoLoaded) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current?.paused) {
      videoRef.current.play();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isHovered && !video.paused) {
      video.pause();
      video.currentTime = 0;
    }
  }, [isHovered]);

  return (
    <Card
      component="a"
      href={permalink}
      target="_blank"
      rel="noopener noreferrer"
      radius="md"
      shadow="sm"
      h={MEDIA_SIZE}
      mah={MEDIA_SIZE}
      orientation="horizontal"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
      data-post-id={id}
    >
      <CardSection
        w={MEDIA_SIZE}
        h={MEDIA_SIZE}
        miw={MEDIA_SIZE}
        mih={MEDIA_SIZE}
        flex="0 0 auto"
        pos="relative"
        style={{ overflow: "hidden" }}
      >
        {isVideo && (
          <video
            ref={videoRef}
            src={mediaUrl}
            muted
            loop
            playsInline
            poster={sizes.small.mediaUrl}
            controls={false}
            onClick={handleVideoClick}
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoLoaded(false)}
            preload="auto"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              objectFit: "cover",
              opacity: isHovered && videoLoaded ? 1 : 0,
              transition: "opacity 0.5s ease",
              cursor: "pointer",
            }}
          />
        )}
        <Image
          src={sizes.small.mediaUrl}
          alt={altText || ""}
          w={MEDIA_SIZE}
          h={MEDIA_SIZE}
          fit="cover"
          style={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.03)" : undefined,
          }}
        />
      </CardSection>
      <Stack
        gap="xs"
        p="sm"
        h="100%"
        style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}
      >
        <Text lineClamp={captionLineClamp(hashtags.length)} style={{ flex: 1, minHeight: 0 }}>
          {prunedCaption}
        </Text>
        {hashtags.length > 0 && (
          <Text size="xs" fw="bold" lineClamp={2} style={{ flexShrink: 0 }}>
            {hashtags.map((h) => `#${h}`).join(" ")}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
